import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { followArtist } from '../../redux/thunk/followedArtistsThunk';
import {
    goArtist,
    suggestionsCache,
    subscribeSuggestions,
    loadFollowSuggestions,
} from './mixpediaHomeData';
import { openMixpediaBrowse } from '../../redux/thunk/appNavThunk';
import TileScroller from './TileScroller';
import CoverArt from '../CoverArt';
import './MixpediaHome.scss';

// Follow suggestions for the Mixpedia home's cold start. The data loader
// (library scan → Mixpedia resolve → artwork enrichment, Popular fallback)
// lives in mixpediaHomeData.js::loadFollowSuggestions so the startup warmer
// can prefetch it alongside the sets/tracks rows; this component just
// subscribes to the shared cache. Each card offers one-tap follow —
// following builds the personalized home rows.
//
// variant: 'grid' = cold-start centerpiece (wrap grid, hero context, Popular
// fallback, spinner). 'row' = standing strip on the populated home — one
// scroller line of library artists not yet followed; no Popular fallback
// (that's cold-start-only content), "Show more" routes to the full page,
// self-retiring once everything's followed. fullPage keeps the header
// visible even when there's nothing left to follow (exit = navbar home).
// hold (row variant): the home passes its sets/tracks loading state here so
// this row keeps its skeleton until those rows are ready — all three rows
// reveal together instead of whichever fetch wins rendering first.
const MixpediaFollowSuggestions = ({ variant = 'grid', fullPage = false, hold = false }) => {
    const dispatch = useDispatch();
    const store = useStore();
    const userId = useSelector((state) => state.auth.user?.id);
    const followed = useSelector((state) => state.followedArtists.artists);
    const followedSet = new Set((followed || []).map(a => (a.artist_name || '').toLowerCase()));

    const cacheHit = suggestionsCache.key === (userId || 'anon');
    const [items, setItems] = useState(cacheHit ? suggestionsCache.items : []);
    const [source, setSource] = useState(cacheHit ? suggestionsCache.source : null);
    const [loading, setLoading] = useState(suggestionsCache.loading);

    useEffect(() => {
        const unsubscribe = subscribeSuggestions((cache) => {
            setItems(cache.items);
            setSource(cache.source);
            setLoading(cache.loading && !cache.items.length);
        });
        loadFollowSuggestions(dispatch, store.getState).catch(() => {});
        return unsubscribe;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId, dispatch, store]);

    const visible = items.filter(a => !followedSet.has(a.name.toLowerCase()));
    const firstChar = (s) => (s || '?').charAt(0).toUpperCase();

    // Bulk follow — every visible suggestion, a few at a time. The component
    // unmounts once follows reach the threshold; the loop keeps running to
    // completion (state updates on the unmounted component are no-ops).
    const [bulk, setBulk] = useState(null);
    const followAll = () => {
        if (bulk || !visible.length) return;
        const queue = [...visible];
        const total = queue.length;
        let done = 0;
        setBulk({ done, total });
        (async () => {
            await Promise.all(Array.from({ length: 5 }, async () => {
                while (queue.length) {
                    const a = queue.shift();
                    try {
                        // quiet: one confirmation card per artist would stack up.
                        await dispatch(followArtist({ name: a.name, artworkUrl: a.artworkUrl, id: a.id, from: 'suggestion', quiet: true }));
                    } catch { /* thunk rolls back + logs */ }
                    done += 1;
                    setBulk({ done, total });
                }
            }));
            setBulk(null);
        })();
    };

    // Row variant counts as pending while its own data resolves OR while the
    // home's sets/tracks rows are still loading (hold) — synced reveal.
    const pending = loading || (variant === 'row' && hold);

    // Full-page mode always renders its header ("done" message included)
    // even after following everything; embedded modes vanish.
    if (!fullPage && !pending && visible.length === 0) return null;
    // The standing row: skeleton while pending (so it loads in step with
    // the sets/tracks rows instead of popping in earlier or later), and
    // never the Popular fallback — that's cold-start-only content.
    if (variant === 'row' && !pending && (source !== 'library' || !visible.length)) return null;
    if (variant === 'row' && pending) {
        return (
            <section className="mh-section">
                <div className="mh-section-header">
                    <h3 className="mh-title">From your library — not following yet</h3>
                </div>
                <div className="mh-slider-row mh-skeleton-row">
                    {Array.from({ length: 8 }, (_, i) => (
                        <div key={i} className="mh-avatar-card mh-skeleton-card">
                            <div className="mh-skeleton-avatar" />
                            <div className="mh-skeleton-line avatar-line" />
                        </div>
                    ))}
                </div>
            </section>
        );
    }

    const cards = visible.map((a) => (
        <div key={a.name} className="mh-avatar-card" onClick={() => goArtist(dispatch, a.name, a.id, a.artworkUrl, 'suggestion')} role="button" tabIndex={0}>
            {a.artworkUrl ? (
                <CoverArt src={a.artworkUrl} size={72} className="mh-avatar" />
            ) : (
                <span className="mh-avatar is-empty">{firstChar(a.name)}</span>
            )}
            <span className="mh-avatar-name">{a.name}</span>
            <button
                className="mh-follow-btn"
                onClick={(e) => {
                    e.stopPropagation();
                    dispatch(followArtist({ name: a.name, artworkUrl: a.artworkUrl, id: a.id, from: 'suggestion' }));
                }}
            >
                + Follow
            </button>
        </div>
    ));

    return (
        <section className="mh-section">
            <div className="mh-section-header">
                <h3 className="mh-title">
                    {variant === 'row'
                        ? 'From your library — not following yet'
                        : source === 'popular' ? 'Popular on Mixpedia' : 'Artists from your library'}
                </h3>
                {variant === 'row' ? (
                    <button className="mh-show-more" onClick={() => dispatch(openMixpediaBrowse('homeLibraryArtists'))}>
                        Show more
                    </button>
                ) : source === 'library' && visible.length > 1 && (
                    <button className="mh-show-more" onClick={followAll} disabled={!!bulk}>
                        {bulk ? `Following ${bulk.done}/${bulk.total}…` : `Follow all (${visible.length})`}
                    </button>
                )}
            </div>
            {!loading && visible.length === 0 ? (
                <p className="mh-suggest-done">You already follow everyone from your library.</p>
            ) : loading && items.length === 0 ? (
                <div className="mh-suggest-loading">
                    <span className="mh-suggest-spinner" />
                </div>
            ) : variant === 'row' ? (
                <TileScroller className="mh-tile-row mh-avatar-tiles">
                    {cards}
                </TileScroller>
            ) : (
                <div className="mh-suggest-grid">
                    {cards}
                </div>
            )}
        </section>
    );
};

export default MixpediaFollowSuggestions;
